import { useRef, useCallback } from 'react';
import type { EnvironmentAnimationState, VisualMode, QualityTier } from '../types';
import { MODE_CONFIG } from '../types';

interface UseEnvironmentAnimationOptions {
  mode: VisualMode;
  reducedMotion: boolean;
  quality: QualityTier;
  animationSpeed: number;
}

export function useEnvironmentAnimation({
  mode,
  reducedMotion,
  quality,
  animationSpeed,
}: UseEnvironmentAnimationOptions) {
  const bubbleCount = quality === 'high' ? 14 : quality === 'balanced' ? 9 : 5;

  const stateRef = useRef<EnvironmentAnimationState>({
    causticPhase: Math.random() * Math.PI * 2,
    bubblePhases: Array.from({ length: bubbleCount }, (_, i) => i * (Math.PI * 2) / bubbleCount + Math.random() * 0.5),
    rayPhase: Math.random() * Math.PI * 2,
  });

  const config = MODE_CONFIG[mode];
  const speedMultiplier = animationSpeed;
  const envMotionMultiplier = config.environmentMotionMultiplier;

  const updateState = useCallback((updater: (prev: EnvironmentAnimationState) => EnvironmentAnimationState) => {
    stateRef.current = updater(stateRef.current);
  }, []);

  const getState = useCallback(() => stateRef.current, []);

  const tick = useCallback(
    (deltaTime: number) => {
      if (reducedMotion) return;

      updateState((prev) => {
        const envSpeed = deltaTime * envMotionMultiplier;

        const nextBubblePhases = prev.bubblePhases.map((phase) =>
          phase + deltaTime * 0.4 * speedMultiplier * envSpeed
        );

        return {
          ...prev,
          causticPhase: prev.causticPhase + deltaTime * 0.12 * speedMultiplier * envSpeed,
          rayPhase: prev.rayPhase + deltaTime * 0.06 * speedMultiplier * envSpeed,
          bubblePhases: nextBubblePhases,
        };
      });
    },
    [reducedMotion, speedMultiplier, envMotionMultiplier, updateState]
  );

  return {
    state: stateRef.current,
    getState,
    tick,
  };
}